/**
 * Import Page
 * Import accounts from Affiliation Cloud into adwstats
 */

import React, { useState, useCallback } from 'react';
import { 
  Cloud, 
  Download, 
  CheckCircle2, 
  Loader2, 
  Eye, 
  EyeOff,
  RefreshCw,
  Tag,
  Building2,
  Globe,
  Key,
  Search,
  Check,
  X,
  FlaskConical,
  AlertTriangle,
  Info,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

// Get adwstats auth token from localStorage
const getAdwstatsToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) {
      const parsed = JSON.parse(auth);
      return parsed.state?.token || null;
    }
  } catch {
    return null;
  }
  return null;
};

// Affiliation Cloud token storage key
const AC_TOKEN_KEY = 'affiliation-cloud-token';
const AC_TOKEN_EXPIRY_HOURS = 12;

interface StoredToken {
  token: string;
  expiresAt: number;
}

// Get stored Affiliation Cloud token (if not expired)
const getStoredACToken = (): string | null => {
  try {
    const stored = localStorage.getItem(AC_TOKEN_KEY);
    if (stored) {
      const parsed: StoredToken = JSON.parse(stored);
      if (parsed.expiresAt > Date.now()) {
        return parsed.token;
      }
      // Token expired, remove it
      localStorage.removeItem(AC_TOKEN_KEY);
    }
  } catch {
    localStorage.removeItem(AC_TOKEN_KEY);
  }
  return null;
};

// Store Affiliation Cloud token with expiry
const storeACToken = (token: string): void => {
  const expiresAt = Date.now() + (AC_TOKEN_EXPIRY_HOURS * 60 * 60 * 1000);
  const stored: StoredToken = { token, expiresAt };
  localStorage.setItem(AC_TOKEN_KEY, JSON.stringify(stored));
};

// Clear stored Affiliation Cloud token
const clearStoredACToken = (): void => {
  localStorage.removeItem(AC_TOKEN_KEY);
};

// Format a timestamp as "X ago"
const formatTimeAgo = (dateStr: string): string => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// Get token expiry time remaining
const getTokenExpiryInfo = (): { hours: number; minutes: number } | null => {
  try {
    const stored = localStorage.getItem(AC_TOKEN_KEY);
    if (stored) {
      const parsed: StoredToken = JSON.parse(stored);
      const remaining = parsed.expiresAt - Date.now();
      if (remaining > 0) {
        const hours = Math.floor(remaining / (60 * 60 * 1000));
        const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
        return { hours, minutes };
      }
    }
  } catch {
    // Ignore
  }
  return null;
};

interface AffiliationCloudAccount {
  ac_id: number;
  advertiser_id: number;
  advertiser_name: string;
  affiliate_system_id: number;
  affiliate_system_name: string;
  brands: Array<{ id: number; name: string }>;
  username: string;
  account_name: string;
  status: string;
  password: string;
  totp_secret: string | null;
  login_url: string | null;
  program: string | null;
  is_imported: boolean;
  // Sync status fields for imported accounts
  local_id: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
}

interface Meta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface TestResultPreview {
  id: string;
  network_id: string;
  parent_network_id: string;
  account_name: string;
  username: string;
  has_password: boolean;
  has_2fa: boolean;
  brands_count: number;
  tags: string[];
}

interface TestResult {
  ac_id: number;
  status: 'will_create' | 'will_update' | 'error';
  existing_id?: string;
  preview: TestResultPreview | null;
  issues: string[];
  error?: string;
}

interface TestSummary {
  total: number;
  will_create: number;
  will_update: number;
  errors: number;
  with_warnings: number;
}

interface TestImportResponse {
  success: boolean;
  test_mode: boolean;
  summary: TestSummary;
  results: TestResult[];
}

export default function Import() {
  // Auth state - check for stored token on init
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(() => getStoredACToken());
  const [isAdmin, setIsAdmin] = useState(() => !!getStoredACToken());
  const [useManualToken, setUseManualToken] = useState(true); // Default to token mode
  const [manualToken, setManualToken] = useState('');
  const [tokenExpiry, setTokenExpiry] = useState<{ hours: number; minutes: number } | null>(() => getTokenExpiryInfo());
  const tokenInputRef = React.useRef<HTMLTextAreaElement>(null);
  
  // Update token expiry every minute
  React.useEffect(() => {
    const interval = setInterval(() => {
      setTokenExpiry(getTokenExpiryInfo());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Account fetching state
  const [accounts, setAccounts] = useState<AffiliationCloudAccount[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Multi-filter state
  const [importFilter, setImportFilter] = useState<'all' | 'imported' | 'not_imported' | 'sync_errors' | 'never_synced'>('all');
  const [statusFilters, setStatusFilters] = useState<Set<string>>(new Set());
  const [platformFilter, setPlatformFilter] = useState<string>('all');

  // Selection state
  const [selectedAccounts, setSelectedAccounts] = useState<Set<number>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Set<number>>(new Set());
  
  // Re-import mode: allow selecting already imported accounts
  const [allowReimport, setAllowReimport] = useState(false);

  // Test import state
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestImportResponse | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);

  // Login to Affiliation Cloud via backend proxy
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      // If using manual token, just set it directly
      if (useManualToken) {
        // Use ref value as fallback for browser automation compatibility
        const tokenValue = manualToken.trim() || tokenInputRef.current?.value.trim() || '';
        if (!tokenValue) {
          throw new Error('Please enter your Affiliation Cloud token');
        }
        setManualToken(tokenValue); // Sync state
        setAuthToken(tokenValue);
        storeACToken(tokenValue); // Store with 12-hour expiry
        setTokenExpiry(getTokenExpiryInfo());
        toast.success('Token stored! Valid for 12 hours. Click "Fetch Accounts" to load accounts.');
        setIsAdmin(true);
        return;
      }

      // Use our backend proxy to avoid CORS issues
      const authResponse = await fetch('/api/import/affiliation-cloud/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdwstatsToken()}`,
        },
        body: JSON.stringify({
          email: email,
          password: password,
        }),
      });

      const authData = await authResponse.json() as { success: boolean; access_token?: string; error?: string };
      
      if (!authResponse.ok || !authData.success) {
        throw new Error(authData.error || 'Login failed. Please check your credentials.');
      }

      setAuthToken(authData.access_token!);
      storeACToken(authData.access_token!); // Store with 12-hour expiry
      setTokenExpiry(getTokenExpiryInfo());
      toast.success('Logged in to Affiliation Cloud (token stored for 12 hours)');
      
      // Note: User needs to be admin to access affiliate accounts
      setIsAdmin(true);
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Failed to login to Affiliation Cloud');
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Fetch ALL accounts from Affiliation Cloud (no pagination)
  const fetchAccounts = useCallback(async () => {
    if (!authToken) return;

    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        per_page: '1000', // Fetch all accounts
      });

      const adwstatsToken = getAdwstatsToken();
      
      // Check if adwstats auth is available
      if (!adwstatsToken) {
        throw new Error('Adwstats session expired. Please log out and log back in.');
      }
      
      const response = await fetch(`/api/import/affiliation-cloud?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adwstatsToken}`,
          'X-Affiliation-Cloud-Token': authToken,
        },
      });

      if (!response.ok) {
        const errorData = await response.json() as { message?: string };
        throw new Error(errorData.message || 'Failed to fetch accounts');
      }

      const data = await response.json() as { data: AffiliationCloudAccount[]; meta: Meta };
      
      // Debug: Log import status
      const importedCount = data.data.filter(a => a.is_imported).length;
      console.log('[Import] Received accounts:', data.data.length, 'Imported:', importedCount);
      if (data.data.length > 0) {
        console.log('[Import] Sample account is_imported:', data.data[0].is_imported, typeof data.data[0].is_imported);
      }
      
      setAccounts(data.data);
      setMeta(data.meta);
      toast.success(`Loaded ${data.data.length} accounts (${importedCount} already imported)`);
    } catch (error: any) {
      console.error('Fetch error:', error);
      toast.error(error.message || 'Failed to fetch accounts');
    } finally {
      setIsLoading(false);
    }
  }, [authToken]);

  // Toggle account selection
  const toggleSelection = (acId: number) => {
    const newSelection = new Set(selectedAccounts);
    if (newSelection.has(acId)) {
      newSelection.delete(acId);
    } else {
      newSelection.add(acId);
    }
    setSelectedAccounts(newSelection);
  };

  // Get unique statuses from accounts for filter dropdown
  const uniqueStatuses = React.useMemo(() => {
    const statuses = new Set(accounts.map(a => a.status));
    return Array.from(statuses).sort();
  }, [accounts]);

  // Get unique platforms (affiliate systems) for filter dropdown
  const uniquePlatforms = React.useMemo(() => {
    const platformCounts = new Map<string, number>();
    accounts.forEach(a => {
      const platform = a.affiliate_system_name || 'Unknown';
      platformCounts.set(platform, (platformCounts.get(platform) || 0) + 1);
    });
    return Array.from(platformCounts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
  }, [accounts]);

  // Filter accounts based on import filter, platform filter, status filters AND search query
  const filteredAccounts = React.useMemo(() => {
    let result = accounts;
    
    // Apply import filter (imported vs not imported vs sync errors)
    if (importFilter === 'imported') {
      result = result.filter(a => a.is_imported);
    } else if (importFilter === 'not_imported') {
      result = result.filter(a => !a.is_imported);
    } else if (importFilter === 'sync_errors') {
      result = result.filter(a => a.is_imported && a.last_sync_status === 'failed');
    } else if (importFilter === 'never_synced') {
      result = result.filter(a => a.is_imported && !a.last_sync_at);
    }
    
    // Apply platform filter
    if (platformFilter !== 'all') {
      result = result.filter(a => a.affiliate_system_name === platformFilter);
    }
    
    // Apply status filters (if any selected, show only matching statuses)
    if (statusFilters.size > 0) {
      result = result.filter(a => statusFilters.has(a.status));
    }
    
    // Apply search filter (case-insensitive)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(a => 
        a.ac_id.toString().includes(query) ||
        a.advertiser_name.toLowerCase().includes(query) ||
        a.account_name?.toLowerCase().includes(query) ||
        a.username?.toLowerCase().includes(query) ||
        a.affiliate_system_name.toLowerCase().includes(query) ||
        a.brands.some(b => b.name.toLowerCase().includes(query))
      );
    }
    
    return result;
  }, [accounts, importFilter, platformFilter, statusFilters, searchQuery]);

  // Check if all visible selectable accounts are selected
  // When allowReimport is true, imported accounts can also be selected
  const selectableAccounts = allowReimport 
    ? filteredAccounts 
    : filteredAccounts.filter(a => !a.is_imported);
  const allVisibleSelected = selectableAccounts.length > 0 && 
    selectableAccounts.every(a => selectedAccounts.has(a.ac_id));
  const someVisibleSelected = selectableAccounts.some(a => selectedAccounts.has(a.ac_id));

  // Select all visible non-imported accounts
  const selectAllVisible = () => {
    const toSelect = selectableAccounts.map(a => a.ac_id);
    setSelectedAccounts(new Set([...selectedAccounts, ...toSelect]));
  };

  // Deselect all visible accounts
  const deselectAllVisible = () => {
    const visibleIds = new Set(filteredAccounts.map(a => a.ac_id));
    const remaining = [...selectedAccounts].filter(id => !visibleIds.has(id));
    setSelectedAccounts(new Set(remaining));
  };

  // Toggle select all visible
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      deselectAllVisible();
    } else {
      selectAllVisible();
    }
  };

  // Select all accounts (when re-import is enabled, includes imported)
  const selectAll = () => {
    const toSelect = allowReimport 
      ? accounts.map(a => a.ac_id)
      : accounts.filter(a => !a.is_imported).map(a => a.ac_id);
    setSelectedAccounts(new Set(toSelect));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedAccounts(new Set());
  };

  // Toggle password visibility for an account
  const togglePasswordVisibility = (acId: number) => {
    const newShow = new Set(showPasswords);
    if (newShow.has(acId)) {
      newShow.delete(acId);
    } else {
      newShow.add(acId);
    }
    setShowPasswords(newShow);
  };

  // Test import - preview without writing to DB
  const handleTestImport = async () => {
    if (selectedAccounts.size === 0) {
      toast.error('Please select accounts to test');
      return;
    }

    setIsTesting(true);
    try {
      const accountsToTest = accounts.filter(a => selectedAccounts.has(a.ac_id));

      const adwstatsToken = getAdwstatsToken();
      
      if (!adwstatsToken) {
        throw new Error('Adwstats session expired. Please log out and log back in.');
      }
      
      const response = await fetch('/api/import/affiliation-cloud?test=true', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adwstatsToken}`,
          'X-Affiliation-Cloud-Token': authToken!,
        },
        body: JSON.stringify({ accounts: accountsToTest }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { message?: string };
        throw new Error(errorData.message || 'Failed to test import');
      }

      const result = await response.json() as TestImportResponse;
      setTestResults(result);
      setShowTestModal(true);
      
      toast.success('Test completed! Review the results.');
    } catch (error: any) {
      console.error('Test error:', error);
      toast.error(error.message || 'Failed to test import');
    } finally {
      setIsTesting(false);
    }
  };

  // Import selected accounts (actual import)
  const handleImport = async () => {
    if (selectedAccounts.size === 0) {
      toast.error('Please select accounts to import');
      return;
    }

    setIsImporting(true);
    try {
      const accountsToImport = accounts.filter(a => selectedAccounts.has(a.ac_id));
      
      // Debug: Log what we're sending
      console.log('[Import] Sending accounts:', accountsToImport.map(a => ({
        ac_id: a.ac_id,
        username: a.username,
        password_present: !!a.password,
        password_length: a.password?.length || 0,
      })));

      const adwstatsTokenForImport = getAdwstatsToken();
      
      if (!adwstatsTokenForImport) {
        throw new Error('Adwstats session expired. Please log out and log back in.');
      }
      
      const response = await fetch('/api/import/affiliation-cloud', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adwstatsTokenForImport}`,
          'X-Affiliation-Cloud-Token': authToken!,
        },
        body: JSON.stringify({ accounts: accountsToImport }),
      });

      if (!response.ok) {
        const errorData = await response.json() as { message?: string };
        throw new Error(errorData.message || 'Failed to import accounts');
      }

      const result = await response.json() as { data: { created: number; updated: number; errors: number } };
      
      if (result.data.created > 0) {
        toast.success(`Created ${result.data.created} new account(s)`);
      }
      if (result.data.updated > 0) {
        toast.success(`Updated ${result.data.updated} existing account(s)`);
      }
      if (result.data.errors > 0) {
        toast.error(`Failed to process ${result.data.errors} account(s)`);
      }

      // Refresh the list
      await fetchAccounts();
      setSelectedAccounts(new Set());
      setShowTestModal(false);
      setTestResults(null);
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import accounts');
    } finally {
      setIsImporting(false);
    }
  };

  // Render Test Results Modal
  const renderTestModal = () => {
    if (!showTestModal || !testResults) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Test Import Results
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Preview of what will happen when you import
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowTestModal(false)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Summary Cards */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white dark:bg-gray-700 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{testResults.summary.total}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{testResults.summary.will_create}</p>
                <p className="text-xs text-green-600 dark:text-green-400">New</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{testResults.summary.will_update}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Update</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{testResults.summary.with_warnings}</p>
                <p className="text-xs text-amber-600 dark:text-amber-400">Warnings</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{testResults.summary.errors}</p>
                <p className="text-xs text-red-600 dark:text-red-400">Errors</p>
              </div>
            </div>
          </div>

          {/* Results List */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {testResults.results.map((result) => {
              const account = accounts.find(a => a.ac_id === result.ac_id);
              
              return (
                <div
                  key={result.ac_id}
                  className={`rounded-xl border p-4 ${
                    result.status === 'will_create'
                      ? 'bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                      : result.status === 'will_update'
                      ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                      : 'bg-red-50/50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                  }`}
                >
                  {/* Status Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm font-medium text-gray-700 dark:text-gray-300">
                        AC #{result.ac_id}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {account?.advertiser_name} → {account?.affiliate_system_name}
                      </span>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
                      result.status === 'will_create'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : result.status === 'will_update'
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    }`}>
                      {result.status === 'will_create' && <CheckCircle2 className="w-3 h-3" />}
                      {result.status === 'will_update' && <RefreshCw className="w-3 h-3" />}
                      {result.status === 'error' && <XCircle className="w-3 h-3" />}
                      {result.status === 'will_create' ? 'New' : result.status === 'will_update' ? 'Update' : 'Error'}
                    </span>
                  </div>

                  {/* Preview of how it will be added */}
                  {result.preview && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3 border border-gray-200 dark:border-gray-600">
                      <h4 className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
                        <ArrowRight className="w-3 h-3" />
                        How it will be stored in adwstats:
                      </h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Account Name:</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{result.preview.account_name}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Network ID:</span>
                          <p className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-block">{result.preview.network_id}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Parent Network:</span>
                          <p className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-block">{result.preview.parent_network_id}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Username:</span>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{result.preview.username}</p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Credentials:</span>
                          <p className="flex items-center gap-2">
                            {result.preview.has_password ? (
                              <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                                <Check className="w-3 h-3" /> Password
                              </span>
                            ) : (
                              <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                                <X className="w-3 h-3" /> No Password
                              </span>
                            )}
                            {result.preview.has_2fa && (
                              <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                <Key className="w-3 h-3" /> 2FA
                              </span>
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Tags:</span>
                          <p className="flex gap-1 flex-wrap">
                            {result.preview.tags.map(tag => (
                              <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-300">
                                {tag}
                              </span>
                            ))}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Issues/Warnings */}
                  {result.issues.length > 0 && (
                    <div className="space-y-1">
                      {result.issues.map((issue, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-amber-700 dark:text-amber-300">{issue}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Error message */}
                  {result.error && (
                    <div className="flex items-start gap-2 text-sm">
                      <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-red-700 dark:text-red-300">{result.error}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={() => setShowTestModal(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              Close
            </button>
            <div className="flex items-center gap-3">
              {(testResults.summary.will_create > 0 || testResults.summary.will_update > 0) && (
                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-green-600/25"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {testResults.summary.will_create > 0 && testResults.summary.will_update > 0
                    ? `Create ${testResults.summary.will_create} & Update ${testResults.summary.will_update}`
                    : testResults.summary.will_create > 0
                    ? `Create ${testResults.summary.will_create} Account(s)`
                    : `Update ${testResults.summary.will_update} Account(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render login form
  if (!authToken) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl mb-4">
                <Cloud className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Connect to Affiliation Cloud
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Login to import accounts from Affiliation Cloud
              </p>
            </div>

            {/* Toggle between login methods */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                type="button"
                onClick={() => setUseManualToken(false)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  !useManualToken 
                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Login with Credentials
              </button>
              <button
                type="button"
                onClick={() => setUseManualToken(true)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  useManualToken 
                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Use Token
              </button>
            </div>

            <form onSubmit={handleLogin} className="space-y-6">
              {useManualToken ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bearer Token
                  </label>
                  <textarea
                    ref={tokenInputRef}
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                    onInput={(e) => setManualToken((e.target as HTMLTextAreaElement).value)}
                    placeholder="Paste your Affiliation Cloud access token here..."
                    rows={4}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all font-mono text-sm"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    To get your token: Login to <a href="https://app.affiliationcloud.com" target="_blank" rel="noopener noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline">Affiliation Cloud</a> as Admin → Open DevTools (F12) → Network tab → Look for any API request → Copy the Authorization header value (without "Bearer ")
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all pr-12"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={isLoggingIn}
                className="w-full py-3 px-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-lg hover:from-violet-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Connecting...
                  </>
                ) : useManualToken ? (
                  <>
                    <Key className="w-5 h-5" />
                    Use This Token
                  </>
                ) : (
                  <>
                    <Cloud className="w-5 h-5" />
                    Connect & Login as Admin
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                {useManualToken ? (
                  <>
                    <strong>How to get your token:</strong> Login to <a href="https://app.affiliationcloud.com" target="_blank" rel="noopener noreferrer" className="underline">Affiliation Cloud</a> → Open DevTools (F12) → Application tab → Cookies → Copy <code className="bg-amber-100 dark:bg-amber-900/50 px-1 rounded">user_access_token</code> value
                  </>
                ) : (
                  <>
                    <strong>Note:</strong> If credentials don't work, use the "Use Token" option instead. You can get your token from the browser's Network tab after logging in manually.
                  </>
                )}
              </p>
            </div>
            
            {/* Token storage info */}
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-300">
                <strong>💾 Token Storage:</strong> Your token will be stored locally for 12 hours, so you won't need to paste it again during that time.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render main import interface
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                  <Cloud className="w-6 h-6 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  Import from Affiliation Cloud
                </h1>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Select and import accounts from Affiliation Cloud. All imports will be tagged as <span className="font-semibold text-violet-600 dark:text-violet-400">Raketech</span>.
              </p>
            </div>
            <div className="flex items-center gap-4">
              {tokenExpiry && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Token expires in {tokenExpiry.hours}h {tokenExpiry.minutes}m
                </span>
              )}
              <button
                onClick={() => {
                  setAuthToken(null);
                  setAccounts([]);
                  setSelectedAccounts(new Set());
                  clearStoredACToken();
                  setTokenExpiry(null);
                  toast.success('Disconnected and token cleared');
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              >
                Disconnect & Clear Token
              </button>
            </div>
          </div>
        </div>

        {/* Search, Filter and Fetch */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          {/* Live Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Live search by AC ID, account, network, system, brand..."
              className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Import Filter */}
          {accounts.length > 0 && (
            <select
              value={importFilter}
              onChange={(e) => setImportFilter(e.target.value as typeof importFilter)}
              className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all min-w-[160px]"
            >
              <option value="all">All ({accounts.length})</option>
              <option value="not_imported">Not Imported ({accounts.filter(a => !a.is_imported).length})</option>
              <option value="imported">Imported ({accounts.filter(a => a.is_imported).length})</option>
              <option value="sync_errors">Sync Errors ({accounts.filter(a => a.is_imported && a.last_sync_status === 'failed').length})</option>
              <option value="never_synced">Never Synced ({accounts.filter(a => a.is_imported && !a.last_sync_at).length})</option>
            </select>
          )}
          
          {/* Platform Filter Dropdown */}
          {accounts.length > 0 && uniquePlatforms.length > 0 && (
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all min-w-[160px]"
            >
              <option value="all">All Platforms</option>
              {uniquePlatforms.map(({ name, count }) => (
                <option key={name} value={name}>{name} ({count})</option>
              ))}
            </select>
          )}
          
          {/* Status Filter Dropdown */}
          {accounts.length > 0 && uniqueStatuses.length > 0 && (
            <select
              value={statusFilters.size === 1 ? [...statusFilters][0] : (statusFilters.size === 0 ? 'all' : 'multiple')}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setStatusFilters(new Set());
                } else {
                  setStatusFilters(new Set([e.target.value]));
                }
              }}
              className="px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all min-w-[120px]"
            >
              <option value="all">All Status</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          )}
          
          <button
            onClick={() => fetchAccounts()}
            disabled={isLoading}
            className="px-6 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {accounts.length === 0 ? 'Fetch Accounts' : 'Refresh'}
          </button>
        </div>

        {/* Re-import Toggle */}
        {accounts.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className={`w-5 h-5 ${allowReimport ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
              <div>
                <span className="font-medium text-gray-900 dark:text-gray-100">Re-import Mode</span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {allowReimport 
                    ? 'You can now select already-imported accounts to update their credentials' 
                    : 'Enable to select and update already-imported accounts'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setAllowReimport(!allowReimport);
                if (!allowReimport === false) {
                  // Clear any selected imported accounts when disabling re-import mode
                  const importedIds = new Set(accounts.filter(a => a.is_imported).map(a => a.ac_id));
                  setSelectedAccounts(new Set([...selectedAccounts].filter(id => !importedIds.has(id))));
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                allowReimport
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {allowReimport ? 'Enabled' : 'Enable'}
            </button>
          </div>
        )}

        {/* Selection Actions */}
        {accounts.length > 0 && (
          <div className="mb-4 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2 flex-wrap">
                <span>Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredAccounts.length}</span> of {accounts.length} accounts</span>
                {importFilter !== 'all' && (
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    importFilter === 'sync_errors' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' : 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-300'
                  }`}>
                    {importFilter === 'not_imported' ? 'Not Imported' : importFilter === 'imported' ? 'Already Imported' : importFilter === 'sync_errors' ? 'Sync Errors' : 'Never Synced'}
                    <button
                      onClick={() => setImportFilter('all')}
                      className="ml-1 hover:text-violet-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {platformFilter !== 'all' && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                    Platform: {platformFilter}
                    <button
                      onClick={() => setPlatformFilter('all')}
                      className="ml-1 hover:text-blue-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {statusFilters.size > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                    Status: {[...statusFilters].join(', ')}
                    <button
                      onClick={() => setStatusFilters(new Set())}
                      className="ml-1 hover:text-emerald-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </span>
              <span className="text-gray-400">|</span>
              <button
                onClick={selectAllVisible}
                disabled={selectableAccounts.length === 0}
                className="text-sm text-violet-600 dark:text-violet-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Select all visible ({selectableAccounts.length})
              </button>
              {selectedAccounts.size > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-gray-600 dark:text-gray-400 hover:underline"
                  >
                    Clear selection ({selectedAccounts.size})
                  </button>
                </>
              )}
            </div>
            {selectedAccounts.size > 0 && (
              <div className="flex items-center gap-3">
                {/* Test Import Button */}
                <button
                  onClick={handleTestImport}
                  disabled={isTesting}
                  className="px-5 py-2.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-all flex items-center gap-2 disabled:opacity-50 border border-amber-200 dark:border-amber-800"
                >
                  {isTesting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <FlaskConical className="w-4 h-4" />
                  )}
                  Test Import ({selectedAccounts.size})
                </button>
                
                {/* Direct Import Button */}
                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-green-600/25"
                >
                  {isImporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Import ({selectedAccounts.size})
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && accounts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-violet-600 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Fetching accounts from Affiliation Cloud...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && accounts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
            <Cloud className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No accounts loaded
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-center max-w-md">
              Click "Fetch Accounts" to load accounts from Affiliation Cloud. You can then select which ones to import.
            </p>
            <button
              onClick={() => fetchAccounts()}
              className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2"
            >
              <Cloud className="w-5 h-5" />
              Fetch Accounts
            </button>
          </div>
        )}

        {/* Accounts Table */}
        {accounts.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                      {selectableAccounts.length > 0 && (
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                          }}
                          onChange={toggleSelectAllVisible}
                          className={`w-4 h-4 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded focus:ring-violet-500 ${
                            allowReimport ? 'text-blue-600' : 'text-violet-600'
                          }`}
                          title={allVisibleSelected ? 'Deselect all visible' : `Select all visible${allowReimport ? ' (including imported)' : ''}`}
                        />
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      AC ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Advertiser (Network)
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Credentials
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Brands
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredAccounts.map((account) => (
                    <tr 
                      key={account.ac_id}
                      className={`
                        ${account.is_imported && account.last_sync_status === 'failed' ? 'bg-red-50/50 dark:bg-red-900/10' : account.is_imported ? 'bg-green-50/50 dark:bg-green-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                        ${selectedAccounts.has(account.ac_id) ? 'bg-violet-50 dark:bg-violet-900/20' : ''}
                        transition-colors
                      `}
                    >
                      <td className="px-4 py-4">
                        {account.is_imported && !allowReimport ? (
                          <div className="flex items-center justify-center w-5 h-5 bg-green-100 dark:bg-green-900/30 rounded-full" title="Already imported">
                            <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                          </div>
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedAccounts.has(account.ac_id)}
                            onChange={() => toggleSelection(account.ac_id)}
                            className={`w-4 h-4 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 rounded focus:ring-violet-500 ${
                              account.is_imported ? 'text-blue-600' : 'text-violet-600'
                            }`}
                            title={account.is_imported ? 'Select for re-import' : 'Select for import'}
                          />
                        )}
                      </td>
                      {/* AC ID */}
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm text-gray-900 dark:text-gray-100">
                          {account.ac_id}
                        </span>
                      </td>
                      {/* Advertiser (Network) */}
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {account.advertiser_name}
                            </span>
                            {account.account_name && account.account_name !== account.advertiser_name && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 block">
                                {account.account_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Platform */}
                      <td className="px-4 py-4">
                        <span className="text-xs text-gray-700 dark:text-gray-300">
                          {account.affiliate_system_name}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-1">
                          {account.is_imported ? (
                            <>
                              <div className="flex items-center gap-1.5">
                                {account.last_sync_status === 'success' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Synced
                                  </span>
                                ) : account.last_sync_status === 'failed' ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                    <XCircle className="w-3 h-3" />
                                    Failed
                                  </span>
                                ) : account.last_sync_at ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                    <CheckCircle2 className="w-3 h-3" />
                                    Imported
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300">
                                    <AlertTriangle className="w-3 h-3" />
                                    Never synced
                                  </span>
                                )}
                              </div>
                              {account.last_sync_at && (
                                <span className="text-[10px] text-gray-400 dark:text-gray-500" title={account.last_sync_at}>
                                  {formatTimeAgo(account.last_sync_at)}
                                </span>
                              )}
                              {account.last_sync_status === 'failed' && account.last_sync_error && (
                                <span className="text-[10px] text-red-500 dark:text-red-400 leading-tight max-w-[220px]" title={account.last_sync_error}>
                                  {account.last_sync_error.length > 60 ? account.last_sync_error.slice(0, 60) + '...' : account.last_sync_error}
                                </span>
                              )}
                            </>
                          ) : account.status === 'ACTIVE' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                              <CheckCircle2 className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              {account.status}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Credentials */}
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-6">User:</span>
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                              {account.username}
                            </code>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 w-6">Pass:</span>
                            {account.password ? (
                              <>
                                <code className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                                  {showPasswords.has(account.ac_id) ? account.password : '••••••••'}
                                </code>
                                <button
                                  onClick={() => togglePasswordVisibility(account.ac_id)}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                  {showPasswords.has(account.ac_id) ? (
                                    <EyeOff className="w-3 h-3" />
                                  ) : (
                                    <Eye className="w-3 h-3" />
                                  )}
                                </button>
                              </>
                            ) : (
                              <span className="text-xs text-blue-500 dark:text-blue-400 flex items-center gap-1" title="Password will be fetched from AC when you import">
                                <Key className="w-3 h-3" />
                                Fetched on import
                              </span>
                            )}
                          </div>
                          {account.totp_secret && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400 w-6">2FA:</span>
                              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                <Key className="w-3 h-3" />
                                Enabled
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Brands */}
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {account.brands.slice(0, 2).map((brand) => (
                            <span
                              key={brand.id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            >
                              {brand.name}
                            </span>
                          ))}
                          {account.brands.length > 2 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                              +{account.brands.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}

        {/* Info card about test import */}
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
          <div className="flex items-start gap-3">
            <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 dark:text-amber-300">Test Before You Import</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Use the <strong>"Test Import"</strong> button to preview what will happen before actually importing. 
                This shows you how each account will be stored, highlights any potential issues, and lets you confirm before committing.
              </p>
            </div>
          </div>
        </div>

        {/* Info card about Raketech tag */}
        <div className="mt-4 p-4 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl">
          <div className="flex items-start gap-3">
            <Tag className="w-5 h-5 text-violet-600 dark:text-violet-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-violet-900 dark:text-violet-300">Automatic Tagging</h4>
              <p className="text-sm text-violet-700 dark:text-violet-400 mt-1">
                All accounts imported from Affiliation Cloud will be automatically tagged with <strong>"Raketech"</strong>. 
                This helps you easily identify and filter accounts by their source.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Results Modal */}
      {renderTestModal()}
    </div>
  );
}
